import { useEffect, useState, useMemo, useRef } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { REASONS, StatusBadge, formatDateTime } from "@/lib/status";
import { AlertTriangle, Loader2, X, Plus, Trash2, Package, ClipboardCheck, FolderOpen, Save, Keyboard, Copy } from "lucide-react";
import CodeKeypad from "@/components/CodeKeypad";
import ProductQuickCreateDialog from "@/components/ProductQuickCreateDialog";
import { useAuth } from "@/context/AuthContext";

export default function NewRequestPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPurchaser = !!(user && (user.role === "admin" || user.role === "purchasing" || (user.extra_roles || []).includes("purchasing")));
  const [codeSuggestions, setCodeSuggestions] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  // Header fields
  const [supplierId, setSupplierId] = useState("");
  const [reason, setReason] = useState("");
  const [internalNote, setInternalNote] = useState("");
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [assignedTo, setAssignedTo] = useState("");
  // Items
  const [items, setItems] = useState([]);
  // Single product input row
  const [codeInput, setCodeInput] = useState("");
  const [qtyInput, setQtyInput] = useState("");
  const codeRef = useRef(null);
  // Mobile: the in-app code keypad is CLOSED by default (system keyboard used).
  // The user opens it with the "Kod tuş takımını aç" button; "Aa" switches back.
  const [padMode, setPadMode] = useState(false);
  // Quick product-create popup (purchasers only) for codes not yet in the catalog.
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickMode, setQuickMode] = useState("new");
  // Conflicts
  const [conflicts, setConflicts] = useState([]);
  const [showConflict, setShowConflict] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});
  // Drafts (kişiye özel taslaklar)
  const [drafts, setDrafts] = useState([]);
  const [showDrafts, setShowDrafts] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [loadedDraftId, setLoadedDraftId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, d, au] = await Promise.all([
          api.get("/suppliers"),
          api.get("/drafts"),
          api.get("/users/assignable"),
        ]);
        setSuppliers(s.data);
        setDrafts(d.data);
        setAssignableUsers(au.data);
        try {
          const last = localStorage.getItem("last_assignee");
          if (last && au.data.some((u) => u.id === last)) setAssignedTo(last);
        } catch (_) { /* noop */ }
      } catch (e) {
        toast.error("Veri yüklenemedi");
      }
    })();
  }, []);

  const loadDraft = (d) => {
    setSupplierId(d.supplier_id || "");
    setReason(d.reason || "");
    setInternalNote(d.internal_note || "");
    setItems((d.items || []).map((it) => ({
      product_code: it.product_code,
      product_name: it.product_name,
      unit: it.unit || "adet",
      quantity: it.quantity,
      note: it.note || "",
    })));
    setLoadedDraftId(d.id);
    setShowDrafts(false);
    toast.success("Taslak yüklendi");
  };

  const deleteDraft = async (id) => {
    if (!window.confirm("Bu taslağı silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/drafts/${id}`);
      setDrafts((prev) => prev.filter((d) => d.id !== id));
      if (loadedDraftId === id) setLoadedDraftId(null);
      toast.success("Taslak silindi");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const saveDraft = async () => {
    const hasContent = !!(supplierId || reason || internalNote.trim() || items.length > 0);
    if (!hasContent) { toast.error("Önce tedarikçi, sebep, not veya ürün ekleyin"); return; }
    setSavingDraft(true);
    try {
      // Tek aktif taslak kalsın: yüklenmiş taslağı güncelle (eskiyi sil)
      if (loadedDraftId) {
        try { await api.delete(`/drafts/${loadedDraftId}`); } catch (_) { /* ignore */ }
      }
      const { data } = await api.post("/drafts", {
        supplier_id: supplierId,
        reason,
        internal_note: internalNote,
        items: items.map(({ product_code, product_name, unit, quantity, note }) => ({
          product_code, product_name, unit, quantity, note: note || "",
        })),
      });
      setDrafts((prev) => [data, ...prev.filter((d) => d.id !== loadedDraftId)]);
      setLoadedDraftId(data.id);
      toast.success("Taslak kaydedildi");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Taslak kaydedilemedi");
    } finally {
      setSavingDraft(false);
    }
  };

  // Search dropdown state
  const [searchOpen, setSearchOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);

  const matchedProduct = useMemo(() => {
    const c = codeInput.trim().toUpperCase();
    if (!c) return null;
    return codeSuggestions.find((x) => x.code === c) || null;
  }, [codeInput, codeSuggestions]);

  // Server-side product search (debounced) — supports very large catalogs
  useEffect(() => {
    const c = codeInput.trim();
    if (!c) { setCodeSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/products", { params: { q: c, limit: 8 } });
        setCodeSuggestions(data);
      } catch { setCodeSuggestions([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [codeInput]);

  // Check conflicts whenever items change
  useEffect(() => {
    if (items.length === 0) { setConflicts([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const results = await Promise.all(
          items.map((it) =>
            api.get("/requests/conflicts", { params: { product_code: it.product_code } })
              .then((r) => r.data).catch(() => [])
          )
        );
        if (cancelled) return;
        // Deduplicate by request id
        const seen = new Set();
        const flat = [];
        results.flat().forEach((c) => { if (!seen.has(c.id)) { seen.add(c.id); flat.push(c); } });
        setConflicts(flat);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [items]);

  const addItem = async () => {
    setErrors({});
    const code = codeInput.trim().toUpperCase();
    if (!code) { setErrors({ code: "Ürün kodu girin" }); return; }
    let product = codeSuggestions.find((x) => x.code === code);
    if (!product) {
      try {
        const { data } = await api.get(`/products/by-code/${encodeURIComponent(code)}`);
        product = data;
      } catch {
        setErrors({ code: "Geçersiz ürün kodu" }); return;
      }
    }
    const qty = Number(qtyInput);
    if (!qty || qty <= 0) { setErrors({ qty: "Miktar girin" }); return; }
    if (items.some((i) => i.product_code === product.code)) {
      // Increment existing
      setItems(items.map((i) => i.product_code === product.code ? { ...i, quantity: i.quantity + qty } : i));
    } else {
      setItems([...items, { product_code: product.code, product_name: product.name, unit: product.unit || "adet", quantity: qty }]);
    }
    setCodeInput("");
    setQtyInput("");
    setSearchOpen(false);
    // Refocus the code field so the next product can be typed without clicking
    setTimeout(() => document.getElementById("product-code-input")?.focus(), 0);
  };

  const removeItem = (code) => setItems(items.filter((i) => i.product_code !== code));
  const updateQty = (code, q) => setItems(items.map((i) => i.product_code === code ? { ...i, quantity: Number(q) || 0 } : i));
  const updateNote = (code, note) => setItems(items.map((i) => i.product_code === code ? { ...i, note } : i));

  // After a purchaser creates a product via the quick popup: if a quantity is
  // already typed, add it straight to the list; otherwise prefill the code and
  // focus quantity so they can continue without re-typing.
  const onProductCreated = (p) => {
    setCodeSuggestions((prev) => [p, ...prev.filter((x) => x.code !== p.code)]);
    setSearchOpen(false);
    setActiveIdx(-1);
    const qty = Number(qtyInput);
    if (qty > 0) {
      setItems((prev) => prev.some((i) => i.product_code === p.code)
        ? prev.map((i) => i.product_code === p.code ? { ...i, quantity: i.quantity + qty } : i)
        : [...prev, { product_code: p.code, product_name: p.name, unit: p.unit || "adet", quantity: qty }]);
      setCodeInput("");
      setQtyInput("");
      setTimeout(() => document.getElementById("product-code-input")?.focus(), 0);
    } else {
      setCodeInput(p.code);
      setTimeout(() => document.getElementById("qty-input")?.focus(), 0);
    }
  };

  const validateHeader = () => {
    const e = {};
    if (!supplierId) e.supplier = "Tedarikçi seçin";
    if (!reason) e.reason = "Talep sebebi seçin";
    if (items.length === 0) e.items = "En az bir ürün ekleyin";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (force = false) => {
    if (!validateHeader()) return;
    if (!force && conflicts.length > 0) {
      setShowConflict(true);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/requests", {
        items: items.map(({ product_code, quantity, note }) => ({ product_code, quantity, note: note || "" })),
        supplier_id: supplierId,
        reason,
        internal_note: internalNote,
        assigned_to: assignedTo || null,
        force,
      });
      toast.success(`Talep oluşturuldu: ${data.request_no}`);
      if (loadedDraftId) {
        try { await api.delete(`/drafts/${loadedDraftId}`); } catch (_) { /* ignore */ }
      }
      setShowConflict(false);
      navigate(`/talepler/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Talep oluşturulamadı");
    } finally {
      setBusy(false);
    }
  };

  const pickProduct = (p) => {
    setCodeInput(p.code);
    setSearchOpen(false);
    setActiveIdx(-1);
    setTimeout(() => document.getElementById("qty-input")?.focus(), 0);
  };

  // Resolve the typed code STRICTLY by exact match (never substitute a fuzzy
  // suggestion). Prevents accidentally selecting an unrelated product when the
  // user types fast and presses Enter.
  const confirmExactCode = async () => {
    const code = codeInput.trim().toUpperCase();
    if (!code) { setErrors({ code: "Ürün kodu girin" }); return; }
    // Qty already filled -> add directly (addItem resolves by exact code).
    if (qtyInput && Number(qtyInput) > 0) { addItem(); return; }
    // Qty empty -> validate the exact code, then jump to the quantity field.
    setErrors({});
    let product = codeSuggestions.find((x) => x.code === code);
    if (!product) {
      try {
        const { data } = await api.get(`/products/by-code/${encodeURIComponent(code)}`);
        product = data;
      } catch {
        setErrors({ code: "Bu kodla birebir eşleşen ürün yok — listeden seçin." });
        return;
      }
    }
    setCodeInput(product.code);
    setSearchOpen(false);
    setActiveIdx(-1);
    setTimeout(() => document.getElementById("qty-input")?.focus(), 0);
  };

  const handleCodeKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (codeSuggestions.length > 0) {
        setSearchOpen(true);
        setActiveIdx((i) => Math.min(i + 1, codeSuggestions.length - 1));
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Escape") {
      setSearchOpen(false);
    } else if (e.key === "Enter") {
      e.preventDefault();
      // Pick a dropdown suggestion ONLY when the user EXPLICITLY highlighted it
      // with the arrow keys (activeIdx >= 0). Otherwise resolve by exact code so
      // a fast typist never lands on an unrelated product.
      if (searchOpen && activeIdx >= 0 && codeSuggestions[activeIdx]) {
        pickProduct(codeSuggestions[activeIdx]);
        return;
      }
      confirmExactCode();
    }
  };

  const totalLines = items.length;
  const totalQty = items.reduce((s, i) => s + (Number(i.quantity) || 0), 0);

  return (
    <div className="max-w-3xl mx-auto fade-up">
      <div className="flex flex-row items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <h1 className="font-display text-xl sm:text-2xl font-semibold tracking-tight">Yeni Talep</h1>
          <p className="text-sm text-slate-500 mt-0.5">Tedarikçi ve sebebi seçtikten sonra ürünleri ekleyin.</p>
        </div>
        <div className="shrink-0">
          <Button
            data-testid="open-drafts-btn"
            type="button"
            variant="outline"
            className="h-10 w-10 p-0 relative"
            onClick={() => setShowDrafts(true)}
            aria-label="Taslaklarım"
            title="Taslaklarım"
          >
            <FolderOpen className="h-5 w-5" />
            {drafts.length > 0 && (
              <span className="absolute -top-1.5 -right-1.5 text-[10px] font-bold bg-slate-900 text-white rounded-full h-4 min-w-[16px] px-1 flex items-center justify-center">{drafts.length}</span>
            )}
          </Button>
        </div>
      </div>

      {/* ============ HEADER (Top) ============ */}
      <div className="bg-white rounded-lg border border-slate-200 p-4 sm:p-6 space-y-4 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Tedarikçi</Label>
            <Select value={supplierId} onValueChange={setSupplierId}>
              <SelectTrigger data-testid="select-supplier" className="mt-1.5 h-12 text-base">
                <SelectValue placeholder="Tedarikçi seçin" />
              </SelectTrigger>
              <SelectContent>
                {suppliers.map((s) => (
                  <SelectItem key={s.id} value={s.id} data-testid={`supplier-opt-${s.id}`}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.supplier && <p className="text-xs text-rose-600 mt-1">{errors.supplier}</p>}
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Talep Sebebi</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="select-reason" className="mt-1.5 h-12 text-base">
                <SelectValue placeholder="Sebep seçin" />
              </SelectTrigger>
              <SelectContent>
                {REASONS.map((r) => <SelectItem key={r} value={r} data-testid={`reason-opt-${r}`}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
            {errors.reason && <p className="text-xs text-rose-600 mt-1">{errors.reason}</p>}
          </div>
        </div>

        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
            Sipariş Hazırlayan <span className="text-slate-400 normal-case font-normal">(opsiyonel — atanan kişinin "Benim Taleplerim"inde de görünür)</span>
          </Label>
          <Select value={assignedTo || "__none__"} onValueChange={(v) => { const val = v === "__none__" ? "" : v; setAssignedTo(val); try { localStorage.setItem("last_assignee", val); } catch (_) { /* noop */ } }}>
            <SelectTrigger data-testid="select-assignee" className="mt-1.5 h-12 text-base">
              <SelectValue placeholder="Atama yok" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__" data-testid="assignee-opt-none">Atama yok</SelectItem>
              {assignableUsers.map((u) => (
                <SelectItem key={u.id} value={u.id} data-testid={`assignee-opt-${u.id}`}>
                  {u.name}{u.role === "warehouse" ? " · Depo" : u.role === "purchasing" ? " · Satın Alma" : u.role === "admin" ? " · Yönetici" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
              İç Not <span className="text-slate-400 normal-case font-normal">(PDF'te görünmez)</span>
            </Label>
            <span data-testid="note-char-count" className={`text-xs tabular-nums ${internalNote.length > 480 ? "text-amber-600 font-medium" : "text-slate-400"}`}>
              {internalNote.length}/500
            </span>
          </div>
          <Textarea
            data-testid="input-internal-note"
            value={internalNote}
            onChange={(e) => {
              const v = e.target.value;
              if (v.length <= 500) setInternalNote(v);
            }}
            maxLength={500}
            placeholder="Sadece sistem içi notlar..."
            className="text-base min-h-[80px]"
          />
        </div>
      </div>

      {/* ============ ITEMS (Bottom) ============ */}
      <div className="bg-white rounded-lg border border-slate-200 p-4 sm:p-6">
        <div className="flex items-end justify-between mb-3">
          <div>
            <h2 className="font-display text-lg font-semibold flex items-center gap-2"><Package className="h-4 w-4" /> Ürünler</h2>
            <p className="text-xs text-slate-500 mt-0.5">Ürün kodu girip miktar yazın, ENTER ile veya + tuşu ile ekleyin. Her ürün için tedarikçiye not ekleyebilirsiniz.</p>
          </div>
          {items.length > 0 && (
            <div className="text-xs text-slate-500">
              <span data-testid="items-count" className="font-medium text-slate-900">{totalLines} kalem</span> · {totalQty} adet/birim
            </div>
          )}
        </div>

        {/* Add row — mobile: tek satır [Kod] [Miktar] [Birim] [+]; desktop: 12-col grid */}
        <div>
          <div className="flex gap-1.5 items-end sm:grid sm:grid-cols-12 sm:gap-2 sm:items-start">
            {/* Kod (arama) — mobilde flex-1, masaüstünde col-span-6 */}
            <div className="flex-1 min-w-0 sm:col-span-6">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 whitespace-nowrap">
                <span className="sm:hidden">Kod</span>
                <span className="hidden sm:inline">Ürün Ara veya Kod Gir</span>
              </Label>
              <div className="relative mt-1.5">
                <Input
                  data-testid="input-product-code"
                  id="product-code-input"
                  ref={codeRef}
                  value={codeInput}
                  onChange={(e) => {
                    setCodeInput(e.target.value);
                    setSearchOpen(true);
                    setActiveIdx(-1);
                  }}
                  onFocus={() => { if (codeInput) setSearchOpen(true); }}
                  onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
                  onKeyDown={handleCodeKey}
                  placeholder="Kod / ürün adı..."
                  className="h-12 sm:h-11 text-base px-2.5 sm:px-3"
                  autoComplete="off"
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  inputMode={padMode ? "none" : "text"}
                />

                {/* Search dropdown — mobilde inputun ÜSTÜnde açılır (klavyeyi/tuş takımını kapatmaz), masaüstünde altında */}
                {searchOpen && codeInput.trim() && (
                  <div
                    className="absolute z-30 left-0 right-0 min-w-[260px] bottom-full mb-1 sm:bottom-auto sm:top-full sm:mb-0 sm:mt-1 bg-white border border-slate-200 rounded-md shadow-lg overflow-hidden"
                  >
                    {/* Quick create — purchasers only, pinned right under the search box */}
                    {isPurchaser && !matchedProduct && (
                      <div data-testid="quick-create-bar" className="border-b border-slate-100 bg-slate-50 p-2 flex flex-wrap items-center gap-2">
                        <span className="text-xs text-slate-500 min-w-0">
                          <span className="font-medium text-slate-700">"{codeInput.trim().toUpperCase()}"</span> kayıtlı değil.
                        </span>
                        <button
                          type="button"
                          data-testid="quick-new-code-btn"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => { setQuickMode("new"); setSearchOpen(false); setQuickOpen(true); }}
                          className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md bg-slate-900 text-white text-xs font-medium hover:bg-slate-800"
                        >
                          <Plus className="h-3.5 w-3.5" /> Yeni kod
                        </button>
                        {codeSuggestions.length > 0 && (
                          <button
                            type="button"
                            data-testid="quick-similar-code-btn"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => { setQuickMode("similar"); setSearchOpen(false); setQuickOpen(true); }}
                            className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md border border-slate-300 text-slate-700 text-xs font-medium hover:bg-slate-100"
                          >
                            <Copy className="h-3.5 w-3.5" /> Benzerinden
                          </button>
                        )}
                      </div>
                    )}
                    {codeSuggestions.length > 0 && (
                      <div data-testid="product-search-results" className="max-h-60 sm:max-h-72 overflow-y-auto thin-scroll">
                        {codeSuggestions.map((p, idx) => (
                          <button
                            key={p.id}
                            type="button"
                            data-testid={`product-result-${p.code}`}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => pickProduct(p)}
                            className={`w-full text-left px-3 min-h-[52px] py-2.5 border-b border-slate-100 last:border-0 flex items-center justify-between gap-3 ${idx === activeIdx ? "bg-slate-50" : "hover:bg-slate-50"}`}
                          >
                            <div className="min-w-0 flex-1">
                              <div className="font-medium text-slate-900 text-sm">{p.code}</div>
                              <div className="text-xs text-slate-500 truncate">{p.name}</div>
                            </div>
                            <span className="text-xs text-slate-400 shrink-0">{p.unit}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {codeSuggestions.length === 0 && !isPurchaser && (
                      <div className="p-3 text-sm text-slate-500 text-center">Sonuç bulunamadı</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Miktar — boşalan yer buraya eklendi (daha geniş) */}
            <div className="w-[76px] shrink-0 sm:w-auto sm:col-span-3">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Miktar</Label>
              <Input
                id="qty-input"
                data-testid="input-quantity"
                type="number"
                min="0"
                step="any"
                inputMode="decimal"
                value={qtyInput}
                onChange={(e) => setQtyInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addItem(); } }}
                placeholder="0"
                className="mt-1.5 h-12 sm:h-11 text-base text-center sm:text-left px-2 sm:px-3"
              />
            </div>

            {/* Birim — dar */}
            <div className="w-[46px] shrink-0 sm:w-auto sm:col-span-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Birim</Label>
              <div data-testid="display-unit" className="mt-1.5 h-12 sm:h-11 px-1 sm:px-3 flex items-center justify-center sm:justify-start bg-slate-50 border border-slate-200 rounded-md text-xs sm:text-sm text-slate-700 truncate">
                {matchedProduct?.unit || "—"}
              </div>
            </div>

            {/* Ekle — küçük + ikonu, ilk dokunuşta anında ekler */}
            <div className="shrink-0 sm:col-span-1">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 hidden sm:block">Ekle</Label>
              <Button
                data-testid="add-item-btn"
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { try { navigator.vibrate?.(8); } catch (_) { /* noop */ } addItem(); }}
                className="h-11 w-11 sm:mt-1.5 sm:h-11 sm:w-full bg-slate-900 hover:bg-slate-800 active:scale-90 transition-transform px-0 rounded-lg touch-manipulation"
                aria-label="Ürün ekle"
              >
                <Plus className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Satır altı yardımcılar (tam genişlik): eşleşme bilgisi, hatalar, kod klavyesi */}
          {matchedProduct && (
            <div data-testid="product-match" className="mt-1.5 text-xs text-emerald-700 truncate">
              ✓ {matchedProduct.code} — {matchedProduct.name} <span className="text-emerald-500">({matchedProduct.unit})</span>
            </div>
          )}
          {errors.code && <p className="text-xs text-rose-600 mt-1">{errors.code}</p>}
          {errors.qty && <p className="text-xs text-rose-600 mt-1">{errors.qty}</p>}

          {padMode ? (
            <CodeKeypad
              inputRef={codeRef}
              value={codeInput}
              onChange={(v) => { setCodeInput(v); setSearchOpen(true); setActiveIdx(-1); }}
              onSwitchToSearch={() => { setPadMode(false); setTimeout(() => codeRef.current?.focus(), 50); }}
            />
          ) : (
            <button
              type="button"
              data-testid="open-pad"
              onClick={() => { setPadMode(true); setTimeout(() => codeRef.current?.focus(), 50); }}
              className="sm:hidden mt-2 w-full h-10 rounded-lg border border-slate-200 bg-white text-slate-600 text-sm font-medium flex items-center justify-center gap-2 active:bg-slate-100"
            >
              <Keyboard className="h-4 w-4" /> Kod tuş takımını aç
            </button>
          )}
        </div>

        {/* Items list */}
        <div className="mt-5">
          {items.length === 0 ? (
            <div data-testid="items-empty" className="text-center text-sm text-slate-400 py-8 border border-dashed border-slate-200 rounded-md">
              Henüz ürün eklenmedi
            </div>
          ) : (
            <div className="border border-slate-200 rounded-md overflow-hidden">
              <div className="hidden sm:grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 text-xs font-bold uppercase tracking-wider text-slate-600">
                <div className="col-span-1">#</div>
                <div className="col-span-2">Kod</div>
                <div className="col-span-5">Açıklama</div>
                <div className="col-span-2 text-right">Miktar</div>
                <div className="col-span-1">Birim</div>
                <div className="col-span-1"></div>
              </div>
              {items.map((it, idx) => (
                <div key={it.product_code} data-testid={`item-row-${it.product_code}`} className="border-t border-slate-100 first:border-t-0 sm:first:border-t">
                  {/* Mobile card layout */}
                  <div className="sm:hidden p-3 flex items-start gap-3">
                    <div className="text-xs text-slate-400 font-medium pt-0.5 w-5">{idx + 1}.</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-semibold text-slate-900 text-sm">{it.product_code}</div>
                        <button
                          data-testid={`item-remove-${it.product_code}`}
                          onClick={() => removeItem(it.product_code)}
                          className="p-1.5 -mr-1 rounded-md hover:bg-rose-50 text-rose-500 shrink-0"
                          aria-label="Kaldır"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="text-sm text-slate-600 mt-0.5 mb-2 line-clamp-2">{it.product_name}</div>
                      <div className="flex items-center gap-2">
                        <Input
                          data-testid={`item-qty-${it.product_code}-mobile`}
                          type="number"
                          min="0"
                          step="any"
                          inputMode="decimal"
                          value={it.quantity}
                          onChange={(e) => updateQty(it.product_code, e.target.value)}
                          className="h-10 text-base text-right max-w-[110px]"
                        />
                        <span className="text-sm text-slate-500">{it.unit}</span>
                      </div>
                    </div>
                  </div>

                  {/* Desktop grid layout */}
                  <div className="hidden sm:grid grid-cols-12 gap-2 px-3 py-3 items-center">
                    <div className="col-span-1 text-xs text-slate-500">{idx + 1}</div>
                    <div className="col-span-2 font-medium text-slate-900 text-sm break-words">{it.product_code}</div>
                    <div className="col-span-5 text-sm text-slate-700 leading-snug break-words">{it.product_name}</div>
                    <div className="col-span-2">
                      <Input
                        data-testid={`item-qty-${it.product_code}`}
                        type="number"
                        min="0"
                        step="any"
                        value={it.quantity}
                        onChange={(e) => updateQty(it.product_code, e.target.value)}
                        className="h-9 text-right"
                      />
                    </div>
                    <div className="col-span-1 text-xs text-slate-500">{it.unit}</div>
                    <div className="col-span-1 flex justify-end">
                      <button
                        onClick={() => removeItem(it.product_code)}
                        className="p-2 rounded-md hover:bg-rose-50 text-rose-500"
                        aria-label="Kaldır"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Per-item supplier note — appears on the PDF & WhatsApp/Mail */}
                  <div className="px-3 pb-3 sm:pl-[3.25rem]">
                    <Input
                      data-testid={`item-note-${it.product_code}`}
                      value={it.note || ""}
                      onChange={(e) => updateNote(it.product_code, e.target.value)}
                      placeholder="Bu ürün için tedarikçiye not (opsiyonel)"
                      className="h-9 text-sm"
                      maxLength={200}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
          {errors.items && <p className="text-xs text-rose-600 mt-2">{errors.items}</p>}
        </div>

        {/* Conflict inline */}
        {conflicts.length > 0 && (
          <div data-testid="conflict-banner" className="mt-4 bg-amber-50 border border-amber-300 text-amber-900 rounded-md p-3 flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0" />
            <div className="text-sm">
              <div className="font-semibold">Bu ürünler için son 30 günde {conflicts.length} açık talep bulunmaktadır.</div>
              <div className="text-amber-800/80 text-xs mt-0.5">Devam etmeden önce mevcut talepleri inceleyin.</div>
            </div>
          </div>
        )}

        <div className="pt-5 space-y-2.5">
          <Button
            data-testid="submit-request-btn"
            disabled={busy || items.length === 0}
            onClick={() => handleSubmit(false)}
            className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white text-base font-medium gap-2"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (
              <>
                <ClipboardCheck className="h-5 w-5" />
                Talebi Oluştur
              </>
            )}
          </Button>
          <Button
            data-testid="save-draft-btn"
            type="button"
            variant="outline"
            disabled={savingDraft}
            onClick={saveDraft}
            className="w-full h-11 gap-2 text-slate-600"
          >
            {savingDraft ? <Loader2 className="h-4 w-4 animate-spin" /> : (
              <>
                <Save className="h-4 w-4" />
                Taslak olarak kaydet
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Conflict dialog */}
      <Dialog open={showConflict} onOpenChange={setShowConflict}>
        <DialogContent data-testid="conflict-dialog" className="max-w-lg border-rose-300">
          <DialogHeader>
            <DialogTitle className="text-rose-700 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" /> Açık Talep Uyarısı
            </DialogTitle>
            <DialogDescription>
              Bu ürünler için son 30 gün içinde aşağıdaki açık talepler bulunmaktadır. Yine de devam etmek ister misiniz?
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-72 overflow-y-auto thin-scroll space-y-2">
            {conflicts.map((c) => (
              <div key={c.id} data-testid={`conflict-row-${c.id}`} className="border border-slate-200 rounded-md p-3 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">{c.request_no}</span>
                  <StatusBadge status={c.status} />
                </div>
                <div className="text-slate-500 text-xs mt-1">{formatDateTime(c.created_at)} • {c.user_name}</div>
                <div className="text-slate-700 mt-1">
                  {(c.items && c.items.length > 0)
                    ? c.items.map((i) => `${i.product_code} (${i.quantity})`).join(" · ")
                    : `${c.product_code} (${c.quantity})`}
                  {" → "}{c.supplier_name}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button data-testid="conflict-cancel" variant="outline" onClick={() => setShowConflict(false)}>
              <X className="h-4 w-4 mr-1" /> Vazgeç
            </Button>
            <Button data-testid="conflict-continue" onClick={() => handleSubmit(true)} disabled={busy} className="bg-slate-900 hover:bg-slate-800">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Yine de Devam Et"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Drafts dialog (kişiye özel taslaklar) */}
      <Dialog open={showDrafts} onOpenChange={setShowDrafts}>
        <DialogContent data-testid="drafts-dialog" className="max-w-lg p-4 sm:p-6 gap-3 sm:gap-4">
          <DialogHeader className="space-y-1">
            <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
              <FolderOpen className="h-5 w-5" /> Taslaklarım
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Yarım kalan talepleri kaydedin; kod hazır olunca buradan seçip devam edin.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[55vh] sm:max-h-80 overflow-y-auto thin-scroll space-y-2 -mx-1 px-1">
            {drafts.length === 0 ? (
              <div data-testid="drafts-empty" className="text-center text-sm text-slate-400 py-6">
                Henüz taslak yok
              </div>
            ) : drafts.map((d) => (
              <div key={d.id} data-testid={`draft-row-${d.id}`} className="border border-slate-200 rounded-md p-2.5 sm:p-3">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="font-medium text-slate-900 text-sm truncate flex-1 min-w-0">{d.supplier_name || "Tedarikçi seçilmedi"}</div>
                  <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full shrink-0">{(d.items || []).length} kalem</span>
                </div>
                <div className="text-xs text-slate-500 truncate">
                  {d.reason || "Sebep yok"} · {formatDateTime(d.created_at)}
                </div>
                {(d.items || []).length > 0 && (
                  <div className="text-xs text-slate-400 truncate mt-0.5">
                    {d.items.map((i) => i.product_code).filter(Boolean).slice(0, 4).join(", ")}{d.items.length > 4 ? "…" : ""}
                  </div>
                )}
                <div className="flex gap-2 mt-2.5">
                  <Button
                    data-testid={`draft-load-${d.id}`}
                    type="button"
                    size="sm"
                    className="flex-1 bg-slate-900 hover:bg-slate-800 h-9"
                    onClick={() => loadDraft(d)}
                  >
                    Devam Et
                  </Button>
                  <button
                    data-testid={`draft-delete-${d.id}`}
                    type="button"
                    onClick={() => deleteDraft(d.id)}
                    className="h-9 w-9 rounded-md border border-slate-200 hover:bg-rose-50 text-rose-500 flex items-center justify-center shrink-0"
                    aria-label="Sil"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter className="mt-0">
            <Button variant="outline" onClick={() => setShowDrafts(false)} className="w-full sm:w-auto h-10">Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ProductQuickCreateDialog
        open={quickOpen}
        onOpenChange={setQuickOpen}
        mode={quickMode}
        initialCode={codeInput}
        suggestions={codeSuggestions}
        onCreated={onProductCreated}
      />
    </div>
  );
}
